import * as React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

// `Table` is a compound component exported as `Object.assign(TableRoot, {…})`,
// which react-refresh's static export check cannot recognise as a component.
/* eslint-disable react-refresh/only-export-components */

/**
 * Table size variants
 * - compact: 36px row height for dense data displays
 * - default: 52px row height for standard use
 */
export type TableSize = 'compact' | 'default';

/**
 * Sort direction for sortable columns
 */
export type SortDirection = 'asc' | 'desc' | null;

/**
 * Props for the Table component
 */
export interface TableProps {
  /**
   * Size variant affecting row height
   * @default 'default'
   */
  size?: TableSize;
  /**
   * Whether rows can be selected
   */
  selectable?: boolean;
  /**
   * Array of selected row IDs (controlled)
   */
  selectedRows?: string[];
  /**
   * Callback when selection changes
   */
  onSelectionChange?: (selectedIds: string[]) => void;
  /**
   * Whether the header should stick to the top when scrolling
   */
  stickyHeader?: boolean;
  /**
   * Additional CSS classes for the table
   */
  className?: string;
  /**
   * Table content (typically Table.Head and Table.Body)
   */
  children: React.ReactNode;
}

/**
 * Props for Table.Head
 */
export interface TableHeadProps {
  /**
   * Header row content (typically Table.Row with Table.HeaderCell)
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for Table.Body
 */
export interface TableBodyProps {
  /**
   * Body row content (typically Table.Row components)
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for Table.Row
 */
export interface TableRowProps {
  /**
   * Unique identifier for selection (required when selectable)
   */
  id?: string;
  /**
   * Row content (typically Table.Cell components)
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Click handler for the row
   */
  onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  /**
   * Whether to show hover highlight on this row.
   * @default true
   */
  hoverable?: boolean;
}

/**
 * Props for Table.HeaderCell
 *
 * Extends the native `<th>` attributes: any extra prop (event handlers,
 * `aria-*`, `data-*`, `id`, `style`…) and the `ref` are forwarded to the
 * `<th>`, so wrappers that inject props into their child — e.g. `Tooltip`,
 * which clones it with `onMouseEnter` / `onMouseLeave` / `onFocus` / `onBlur` /
 * `aria-describedby` and a ref — work on a header cell.
 */
export interface TableHeaderCellProps
  extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'align' | 'width' | 'children' | 'className'> {
  /**
   * Ref to the `<th>` element (React 19 ref-as-prop)
   */
  ref?: React.Ref<HTMLTableCellElement>;
  /**
   * Whether this column is sortable
   */
  sortable?: boolean;
  /**
   * Current sort direction (null = not sorted)
   */
  sortDirection?: SortDirection;
  /**
   * Callback when sort is triggered
   */
  onSort?: () => void;
  /**
   * Text alignment
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';
  /**
   * Column width (CSS value or number for pixels)
   */
  width?: string | number;
  /**
   * Header cell content
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for Table.Cell
 */
export interface TableCellProps {
  /**
   * Text alignment
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';
  /**
   * Cell content
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Column span
   */
  colSpan?: number;
}

// Context for passing table state to compound components
interface TableContextValue {
  size: TableSize;
  selectable: boolean;
  selectedRows: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  stickyHeader: boolean;
  rowIds: string[];
  registerRowId: (id: string) => void;
  unregisterRowId: (id: string) => void;
}

const TableContext = React.createContext<TableContextValue | null>(null);

function useTableContext(): TableContextValue {
  const context = React.useContext(TableContext);
  if (!context) {
    throw new Error('Table compound components must be used within Table');
  }
  return context;
}

/**
 * Hook for managing select all state and handler
 * Shared between TableHeaderCell and TableCheckboxHeaderCell to avoid duplication
 */
function useSelectAll() {
  const { selectedRows, onSelectionChange, rowIds } = useTableContext();

  const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedRows.includes(id));
  const someSelected = rowIds.some((id) => selectedRows.includes(id)) && !allSelected;

  const handleSelectAll = React.useCallback(
    (checked: boolean): void => {
      if (checked) {
        const newSelection = Array.from(new Set([...selectedRows, ...rowIds]));
        onSelectionChange(newSelection);
      } else {
        onSelectionChange(selectedRows.filter((id) => !rowIds.includes(id)));
      }
    },
    [selectedRows, rowIds, onSelectionChange]
  );

  return { allSelected, someSelected, handleSelectAll };
}

/**
 * Row height classes for each size variant
 */
const rowHeightClasses: Record<TableSize, string> = {
  compact: 'h-[36px]',
  default: 'h-[52px]',
};

/**
 * Text alignment classes
 */
const alignClasses: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/**
 * Map sort direction to aria-sort attribute value
 */
const ariaSortMap: Record<string, 'ascending' | 'descending'> = {
  asc: 'ascending',
  desc: 'descending',
};

/**
 * Get aria-sort value for a sort direction
 */
function getAriaSort(sortDirection: SortDirection): 'ascending' | 'descending' | undefined {
  if (sortDirection === null) return undefined;
  return ariaSortMap[sortDirection];
}

/**
 * Build row classes based on state
 */
function buildRowClasses(
  size: TableSize,
  isSelected: boolean,
  hasClickHandler: boolean,
  hoverable: boolean,
  className: string
): string {
  const classes = [
    rowHeightClasses[size],
    'border-b border-secondary-100',
    'transition-colors duration-150 ease-out',
    className,
  ];

  // Add selection/hover background class
  if (isSelected) {
    classes.push('bg-primary-50');
  } else if (hoverable) {
    classes.push('hover:bg-secondary-100');
  }

  // Add cursor style for clickable rows
  if (hasClickHandler) {
    classes.push('cursor-pointer');
  }

  return classes.filter(Boolean).join(' ');
}

/**
 * Checkbox component for table selection
 */
function TableCheckbox({
  checked,
  indeterminate,
  onChange,
  disabled,
  'aria-label': ariaLabel,
}: Readonly<{
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  'aria-label': string;
}>) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    /* v8 ignore start */
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate || false;
    }
    /* v8 ignore stop */
  }, [indeterminate]);

  return (
    <label className="relative inline-flex items-center justify-center w-full h-full cursor-pointer">
      <input
        ref={inputRef}
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
        aria-label={ariaLabel}
      />
      <span
        className={[
          'w-[18px] h-[18px]',
          'relative flex items-center justify-center shrink-0',
          'rounded-[4px] border-2',
          'transition-all duration-150 ease-out',
          !disabled && 'border-secondary-400',
          !disabled && 'peer-checked:bg-primary-700 peer-checked:border-primary-700',
          !disabled && indeterminate && 'bg-primary-700 border-primary-700',
          !disabled && 'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-200 peer-focus-visible:ring-offset-1',
          /* v8 ignore next */
          disabled && 'border-secondary-300 bg-secondary-100',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden="true"
      >
        {/* Checkmark icon */}
        <svg
          className={[
            'w-3 h-3 text-white',
            'opacity-0 peer-checked:opacity-100',
            indeterminate && 'hidden',
          ]
            .filter(Boolean)
            .join(' ')}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="2 6 5 9 10 3" />
        </svg>
        {/* Indeterminate dash icon */}
        {indeterminate && (
          <svg
            className="w-3 h-3 text-white"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="2" y1="6" x2="10" y2="6" />
          </svg>
        )}
      </span>
    </label>
  );
}

/**
 * Table Head component - wraps header row
 */
function TableHead({ children, className = '' }: Readonly<TableHeadProps>) {
  const { stickyHeader } = useTableContext();

  return (
    <thead
      className={[
        'bg-secondary-50',
        stickyHeader && 'sticky top-0 z-10',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </thead>
  );
}

TableHead.displayName = 'Table.Head';

/**
 * Table Body component - wraps body rows
 */
function TableBody({ children, className = '' }: Readonly<TableBodyProps>) {
  return <tbody className={className}>{children}</tbody>;
}

TableBody.displayName = 'Table.Body';

/**
 * Table Row component
 */
function TableRow({ id, children, className = '', onClick, hoverable = true }: Readonly<TableRowProps>) {
  const { size, selectable, selectedRows, onSelectionChange, registerRowId, unregisterRowId } =
    useTableContext();
  const isSelected = id ? selectedRows.includes(id) : false;

  // Register/unregister row ID for select all functionality
  React.useEffect(() => {
    if (id) {
      registerRowId(id);
      return () => {
        unregisterRowId(id);
      };
    }
  }, [id, registerRowId, unregisterRowId]);

  const handleCheckboxChange = (checked: boolean) => {
    /* v8 ignore start */
    if (!id) return;
    /* v8 ignore stop */
    if (checked) {
      onSelectionChange([...selectedRows, id]);
    } else {
      onSelectionChange(selectedRows.filter((rowId) => rowId !== id));
    }
  };

  const rowClasses = buildRowClasses(size, isSelected, Boolean(onClick), hoverable, className);

  return (
    <tr className={rowClasses} onClick={onClick} aria-selected={selectable ? isSelected : undefined}>
      {selectable && id && (
        <td className="w-[48px] min-w-[48px] px-3">
          <TableCheckbox
            checked={isSelected}
            onChange={handleCheckboxChange}
            /* v8 ignore start */
            aria-label={id ? `Select row ${id}` : 'Select row'}
            /* v8 ignore stop */
          />
        </td>
      )}
      {children}
    </tr>
  );
}

TableRow.displayName = 'Table.Row';

/**
 * Table Header Cell component
 * Note: For checkbox header cells in selectable tables, use Table.CheckboxHeaderCell instead.
 *
 * Remaining `<th>` props and the `ref` are forwarded to the `<th>`. On a
 * sortable header a consumer `onClick` / `onKeyDown` still runs (before the
 * sort handling); `style` is merged under the `width` style.
 */
function TableHeaderCell({
  sortable = false,
  sortDirection = null,
  onSort,
  align = 'left',
  width,
  children,
  className = '',
  style,
  ref,
  ...thProps
}: Readonly<TableHeaderCellProps>) {
  const { size } = useTableContext();
  const { onClick, onKeyDown } = thProps;

  const handleSortClick = (e: React.MouseEvent<HTMLTableCellElement>) => {
    onClick?.(e);
    if (sortable && onSort) {
      onSort();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableCellElement>) => {
    onKeyDown?.(e);
    if (sortable && onSort && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onSort();
    }
  };

  // Width style
  const widthStyle: React.CSSProperties = { ...style };
  if (width !== undefined) {
    widthStyle.width = typeof width === 'number' ? `${String(width)}px` : width;
    widthStyle.minWidth = typeof width === 'number' ? `${String(width)}px` : width;
  }

  // Determine aria-sort value using lookup instead of nested ternary
  const ariaSort = getAriaSort(sortDirection);

  // Size-based header height and padding
  const headerSizeClasses = size === 'compact' ? 'h-[36px] px-3 py-1' : 'h-[44px] px-3 py-2';

  const cellClasses = [
    headerSizeClasses,
    'text-xs uppercase font-semibold',
    'text-secondary-600',
    'tracking-wide',
    alignClasses[align],
    sortable && 'cursor-pointer select-none',
    sortable && 'hover:text-secondary-700',
    sortable && 'transition-colors duration-150 ease-out',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <th
      {...thProps}
      ref={ref}
      scope="col"
      className={cellClasses}
      style={widthStyle}
      aria-sort={ariaSort}
      {...(sortable && {
        role: 'button',
        tabIndex: 0,
        onClick: handleSortClick,
        onKeyDown: handleKeyDown,
      })}
    >
      <div className={['inline-flex items-center gap-1', align === 'right' && 'flex-row-reverse'].filter(Boolean).join(' ')}>
        <span>{children}</span>
        {sortable && (
          <span className="inline-flex flex-col -space-y-1" aria-hidden="true">
            <ChevronUp
              className={[
                'w-3 h-3',
                sortDirection === 'asc' ? 'text-primary-700' : 'text-secondary-400',
              ].join(' ')}
            />
            <ChevronDown
              className={[
                'w-3 h-3',
                sortDirection === 'desc' ? 'text-primary-700' : 'text-secondary-400',
              ].join(' ')}
            />
          </span>
        )}
      </div>
    </th>
  );
}

TableHeaderCell.displayName = 'Table.HeaderCell';

/**
 * Checkbox header cell component
 */
function TableCheckboxHeaderCell() {
  const { selectable, size } = useTableContext();
  const { allSelected, someSelected, handleSelectAll } = useSelectAll();

  if (!selectable) return null;

  const headerHeight = size === 'compact' ? 'h-[36px]' : 'h-[44px]';

  return (
    <th scope="col" className={`${headerHeight} w-[48px] min-w-[48px] px-3 bg-secondary-50`}>
      <TableCheckbox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAll}
        aria-label="Select all rows"
      />
    </th>
  );
}

TableCheckboxHeaderCell.displayName = 'Table.CheckboxHeaderCell';

/**
 * Table Cell component
 */
function TableCell({ align = 'left', children, className = '', colSpan }: Readonly<TableCellProps>) {
  const { size } = useTableContext();

  // Size-based padding
  const paddingClasses = size === 'compact' ? 'px-3 py-1' : 'px-3 py-2.5';

  const cellClasses = [
    paddingClasses,
    'text-sm',
    'text-secondary-700',
    alignClasses[align],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <td className={cellClasses} colSpan={colSpan}>
      {children}
    </td>
  );
}

TableCell.displayName = 'Table.Cell';

/**
 * Table component for displaying tabular data with sorting, selection, and more.
 *
 * Designed for insurance industry applications where clear data presentation
 * is critical for policies, claims, and financial data.
 *
 * Features:
 * - Two size variants (compact: 36px, default: 52px row height)
 * - Row selection with checkboxes (single and bulk)
 * - Sortable column headers with visual indicators
 * - Sticky header option for long tables
 * - Proper accessibility with ARIA attributes
 * - Compound components (Head, Body, Row, HeaderCell, Cell)
 *
 * @example
 * // Basic table
 * <Table>
 *   <Table.Head>
 *     <Table.Row>
 *       <Table.HeaderCell>Policy Number</Table.HeaderCell>
 *       <Table.HeaderCell>Client</Table.HeaderCell>
 *       <Table.HeaderCell align="right">Premium</Table.HeaderCell>
 *     </Table.Row>
 *   </Table.Head>
 *   <Table.Body>
 *     <Table.Row>
 *       <Table.Cell>POL-2024-001</Table.Cell>
 *       <Table.Cell>Acme Corp</Table.Cell>
 *       <Table.Cell align="right">$12,450</Table.Cell>
 *     </Table.Row>
 *   </Table.Body>
 * </Table>
 *
 * @example
 * // Table with selection and sorting
 * <Table
 *   selectable
 *   selectedRows={selectedIds}
 *   onSelectionChange={setSelectedIds}
 * >
 *   <Table.Head>
 *     <Table.Row>
 *       <Table.HeaderCell
 *         sortable
 *         sortDirection={sortField === 'policy' ? sortDir : null}
 *         onSort={() => handleSort('policy')}
 *       >
 *         Policy Number
 *       </Table.HeaderCell>
 *       ...
 *     </Table.Row>
 *   </Table.Head>
 *   ...
 * </Table>
 */
const TableRoot = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      size = 'default',
      selectable = false,
      selectedRows: controlledSelectedRows,
      onSelectionChange,
      stickyHeader = false,
      className = '',
      children,
    },
    ref
  ) => {
    // Internal state for row IDs (for select all)
    const [rowIds, setRowIds] = React.useState<string[]>([]);

    // Internal state for uncontrolled selection
    const [internalSelectedRows, setInternalSelectedRows] = React.useState<string[]>([]);

    // Determine if controlled
    const isControlled = controlledSelectedRows !== undefined;
    const selectedRows = isControlled ? controlledSelectedRows : internalSelectedRows;

    const handleSelectionChange = React.useCallback(
      (newSelection: string[]) => {
        if (!isControlled) {
          setInternalSelectedRows(newSelection);
        }
        onSelectionChange?.(newSelection);
      },
      [isControlled, onSelectionChange]
    );

    const registerRowId = React.useCallback((id: string) => {
      setRowIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }, []);

    const unregisterRowId = React.useCallback((id: string) => {
      setRowIds((prev) => prev.filter((rowId) => rowId !== id));
    }, []);

    const contextValue = React.useMemo<TableContextValue>(
      () => ({
        size,
        selectable,
        selectedRows,
        onSelectionChange: handleSelectionChange,
        stickyHeader,
        rowIds,
        registerRowId,
        unregisterRowId,
      }),
      [
        size,
        selectable,
        selectedRows,
        handleSelectionChange,
        stickyHeader,
        rowIds,
        registerRowId,
        unregisterRowId,
      ]
    );

    const tableClasses = [
      'w-full',
      'border-collapse',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <TableContext.Provider value={contextValue}>
        <table ref={ref} className={tableClasses}>
          {children}
        </table>
      </TableContext.Provider>
    );
  }
);

TableRoot.displayName = 'Table';

/**
 * Table compound component with Head, Body, Row, HeaderCell, Cell
 */
export const Table = Object.assign(TableRoot, {
  Head: TableHead,
  Body: TableBody,
  Row: TableRow,
  HeaderCell: TableHeaderCell,
  CheckboxHeaderCell: TableCheckboxHeaderCell,
  Cell: TableCell,
});
